import {
  ConfirmationResult,
  EmailAuthProvider,
  RecaptchaVerifier,
  UserCredential,
  deleteUser,
  getAdditionalUserInfo,
  inMemoryPersistence,
  linkWithCredential,
  setPersistence,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut,
  unlink,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';

export const RECAPTCHA_CONTAINER_ID = 'register-recaptcha-container';
export const RECAPTCHA_WRAPPER_ID = 'register-recaptcha-wrapper';

declare global {
  interface Window {
    registerRecaptchaVerifier?: RecaptchaVerifier;
  }
}

/** Convert a validated 10-digit US phone to E.164 (`+1XXXXXXXXXX`). */
export function toE164US(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  throw new Error('Please enter a valid 10-digit US phone number');
}

/** Obscure a US phone for display, e.g. `(***) ***-1234`. */
export function formatObscuredPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) {
    return phone;
  }
  return `(***) ***-${digits.slice(-4)}`;
}

export function getPhoneAuthErrorMessage(error: unknown, fallback: string): string {
  const code = (error as { code?: string })?.code;
  switch (code) {
    case 'auth/invalid-phone-number':
      return 'Please enter a valid US phone number.';
    case 'auth/too-many-requests':
      return 'Too many verification attempts. Please try again later.';
    case 'auth/operation-not-allowed':
      return 'Phone verification is not enabled for this Firebase project.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for phone verification in Firebase.';
    case 'auth/quota-exceeded':
      return 'SMS quota exceeded. Please try again later.';
    case 'auth/captcha-check-failed':
    case 'auth/invalid-app-credential':
      return 'reCAPTCHA verification failed. Please refresh the page and try again.';
    case 'auth/argument-error':
      return 'Phone verification failed to initialize. Please refresh the page and try again.';
    case 'auth/missing-phone-number':
      return 'Please enter a phone number.';
    case 'auth/email-already-in-use':
    case 'auth/credential-already-in-use':
      return 'This email is already associated with another account.';
    case 'auth/provider-already-linked':
      return 'This phone number is already registered with an email account.';
    default:
      return code ? `${fallback} (${code})` : fallback;
  }
}

/**
 * Thrown when OTP confirm succeeded but a later step (e.g. email link) failed.
 * The SMS code is spent. Account conflicts should close the dialog and send the
 * user to login / change the conflicting field; other failures should prompt a resend.
 */
export type OtpAccountConflictKind = 'email' | 'phone';

export class OtpSessionConsumedError extends Error {
  readonly code = 'otp/session-consumed';
  readonly causeError: unknown;
  readonly conflictKind: OtpAccountConflictKind | null;
  readonly isAccountConflict: boolean;

  constructor(causeError: unknown) {
    const causeCode = (causeError as { code?: string })?.code;
    let conflictKind: OtpAccountConflictKind | null = null;
    if (causeCode === 'auth/email-already-in-use' || causeCode === 'auth/credential-already-in-use') {
      conflictKind = 'email';
    } else if (causeCode === 'auth/provider-already-linked') {
      // Phone user already has email/password — resending SMS cannot unlink/relink it.
      conflictKind = 'phone';
    }

    const reason = getPhoneAuthErrorMessage(
      causeError,
      'Verification succeeded, but we could not finish linking your account.',
    );
    const nextStep =
      conflictKind === 'email'
        ? 'Please log in with that email, or use a different email to register.'
        : conflictKind === 'phone'
          ? 'Please log in, or use a different phone number to register.'
          : 'Please request a new code.';

    super(`${reason} ${nextStep}`);
    this.name = 'OtpSessionConsumedError';
    this.causeError = causeError;
    this.conflictKind = conflictKind;
    this.isAccountConflict = conflictKind !== null;
  }
}

function resetRecaptchaContainer() {
  const wrapper = document.getElementById(RECAPTCHA_WRAPPER_ID);
  if (wrapper) {
    wrapper.innerHTML = `<div id="${RECAPTCHA_CONTAINER_ID}"></div>`;
    return;
  }

  let container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = RECAPTCHA_CONTAINER_ID;
    document.body.appendChild(container);
  } else {
    container.innerHTML = '';
  }
}

function clearRecaptchaVerifier() {
  try {
    window.registerRecaptchaVerifier?.clear();
  } catch {
    // verifier may already be disposed
  }
  window.registerRecaptchaVerifier = undefined;
  resetRecaptchaContainer();
}

async function ensureRecaptchaVerifier(): Promise<RecaptchaVerifier> {
  if (typeof window === 'undefined') {
    throw new Error('reCAPTCHA is only available in the browser');
  }

  if (!document.getElementById(RECAPTCHA_CONTAINER_ID)) {
    resetRecaptchaContainer();
  }

  if (!window.registerRecaptchaVerifier) {
    window.registerRecaptchaVerifier = new RecaptchaVerifier(
      RECAPTCHA_CONTAINER_ID,
      {
        size: 'invisible',
        callback: () => {
          // Invoked after reCAPTCHA is solved; signInWithPhoneNumber continues.
        },
        'expired-callback': () => {
          clearRecaptchaVerifier();
        },
      },
      auth,
    );
    await window.registerRecaptchaVerifier.render();
  }

  return window.registerRecaptchaVerifier;
}

/**
 * Thrown when deleteUser was required but failed. Anonymous session is still
 * restored; callers must treat the phone+email Firebase user as still present.
 */
export class FirebaseUserDeleteFailedError extends Error {
  readonly code = 'auth/delete-user-failed';
  readonly causeError: unknown;

  constructor(causeError: unknown) {
    super('Failed to delete Firebase user during registration rollback.');
    this.name = 'FirebaseUserDeleteFailedError';
    this.causeError = causeError;
  }
}

/**
 * Return to anonymous auth so enrollment API calls continue to work.
 * When `deleteCurrentUser` is true, removes the incomplete phone/email Firebase
 * user first so retries are not blocked by already-in-use errors.
 *
 * If delete fails, signs out + anonymous anyway, then throws
 * FirebaseUserDeleteFailedError so callers (e.g. cleanupFailedEnrollment) can
 * block re-register — the stuck phone+email user still exists.
 */
export async function restoreAnonymousSession(options: { deleteCurrentUser?: boolean } = {}): Promise<void> {
  clearRecaptchaVerifier();

  let deleteFailedError: unknown;
  const user = auth.currentUser;
  if (options.deleteCurrentUser && user && !user.isAnonymous) {
    try {
      // deleteUser also signs the user out on success.
      await deleteUser(user);
    } catch (error) {
      console.error('Failed to delete incomplete Firebase user; falling back to signOut', error);
      deleteFailedError = error;
      await signOut(auth);
    }
  } else {
    await signOut(auth);
  }

  await signInAnonymously(auth);

  if (deleteFailedError !== undefined) {
    throw new FirebaseUserDeleteFailedError(deleteFailedError);
  }
}

/**
 * Undo registration auth changes after a failed enrollment step (Phoenix, or while
 * still signed in as the OTP-linked user).
 * - New phone users are deleted so email/phone stay reusable.
 * - Existing phone accounts are preserved; only a newly linked email is unlinked.
 *
 * Do not use this for Falcon redeem prep — Falcon must keep the phone+email user
 * (see switchToAnonymousForFalconRedeem).
 */
export async function rollbackRegistrationAuth(isNewPhoneUser: boolean): Promise<void> {
  if (isNewPhoneUser) {
    await restoreAnonymousSession({ deleteCurrentUser: true });
    return;
  }

  const user = auth.currentUser;
  if (user && !user.isAnonymous) {
    try {
      await unlink(user, EmailAuthProvider.PROVIDER_ID);
    } catch (error) {
      // Link may never have succeeded; safe to continue with sign-out.
      console.error('Failed to unlink email during registration rollback', error);
    }
  }

  await restoreAnonymousSession({ deleteCurrentUser: false });
}

/**
 * Falcon redeem requires an anonymous Firebase session (EXT-325), but the
 * OTP-linked phone+email user must remain in the project. Sign out of that
 * account and return to anonymous without deleteUser / unlink.
 */
export async function switchToAnonymousForFalconRedeem(): Promise<void> {
  await restoreAnonymousSession({ deleteCurrentUser: false });
}

/**
 * After Falcon prep/redeem fails, the OTP-linked phone+email user may still exist
 * while the session is anonymous or null (e.g. signed out mid-switch). Re-auth with
 * email/password when needed, then roll back so email/phone are reusable (Falcon
 * never created a backend account, so "log in" is not a valid recovery path).
 */
export async function rollbackFalconLinkedUserAfterFailedRedeem(
  email: string,
  password: string,
  isNewPhoneUser: boolean,
): Promise<void> {
  const current = auth.currentUser;
  const alreadySignedInAsLinkedUser =
    !!current && !current.isAnonymous && current.email?.toLowerCase() === email.toLowerCase();

  if (!alreadySignedInAsLinkedUser) {
    await setPersistence(auth, inMemoryPersistence);
    await signInWithEmailAndPassword(auth, email, password);
  }

  await rollbackRegistrationAuth(isNewPhoneUser);
}

/**
 * Send an SMS OTP via Firebase Phone Auth.
 * Uses in-memory persistence so the phone session does not stick around.
 */
export async function sendPhoneOtp(phone: string): Promise<ConfirmationResult> {
  const e164 = toE164US(phone);
  await setPersistence(auth, inMemoryPersistence);

  clearRecaptchaVerifier();
  const verifier = await ensureRecaptchaVerifier();

  try {
    return await signInWithPhoneNumber(auth, e164, verifier);
  } catch (error) {
    clearRecaptchaVerifier();
    throw error;
  }
}

export type VerifyPhoneOtpAndLinkResult = {
  userCredential: UserCredential;
  /** True when OTP confirm created a new Firebase user (vs signing into an existing phone account). */
  isNewPhoneUser: boolean;
};

/**
 * Confirm the SMS code on the phone-auth user, then link email/password
 * onto that same Firebase user so registration does not create a second account.
 *
 * If linking fails after OTP confirm:
 * - newly created phone users are deleted
 * - pre-existing phone accounts are only signed out (never deleted)
 */
export async function verifyPhoneOtpAndLinkEmail(
  confirmation: ConfirmationResult,
  code: string,
  email: string,
  password: string,
): Promise<VerifyPhoneOtpAndLinkResult> {
  const userCredentialPhone = await confirmation.confirm(code);
  const isNewPhoneUser = Boolean(getAdditionalUserInfo(userCredentialPhone)?.isNewUser);
  clearRecaptchaVerifier();

  try {
    const emailCredential = EmailAuthProvider.credential(email, password);
    const userCredential = await linkWithCredential(userCredentialPhone.user, emailCredential);
    return { userCredential, isNewPhoneUser };
  } catch (error) {
    // Only delete users created in this attempt — never wipe an existing phone account.
    // Always surface OtpSessionConsumedError so the parent clears the spent OTP session,
    // even if restore itself fails.
    try {
      await restoreAnonymousSession({ deleteCurrentUser: isNewPhoneUser });
    } catch (restoreError) {
      console.error('Failed to restore anonymous session after link failure', restoreError);
    }
    throw new OtpSessionConsumedError(error);
  }
}
